/**
 * /settings/cert — gestión del certificado ECD BYO del partner.
 *
 * Backend (taxor, /api/v1/admin/tax-clients/:client_id/cert*):
 *   GET    /cert          — cert activo (o has_byo_cert=false → modo mandato)
 *   POST   /cert/upload   — multipart {file: .p12, password, expected_nit?}
 *   DELETE /cert          — archive (body {reason}) → vuelve a mandato PT
 *   GET    /cert/history  — historial de certs subidos
 *
 * El :client_id es la org activa del JWT (resuelta vía tax-account
 * /auth/me — hook useActiveOrg). El guard RequireClientMatchesOrg del
 * backend rechaza con 403 cualquier otro id.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Archive, Loader2, ShieldCheck, Upload } from 'lucide-react'
import { toast } from 'sonner'

import { AppShell } from '@/components/layout/app-shell'
import { certApi, type CertInfo } from '@/lib/api'
import { useActiveOrg } from '@/hooks/use-active-org'

export function CertPage() {
  const { activeOrgId, isLoading: orgLoading, isError: orgError } = useActiveOrg()

  const cert = useQuery({
    queryKey: ['cert', activeOrgId],
    queryFn: () => certApi.get(activeOrgId!),
    enabled: !!activeOrgId,
  })
  const history = useQuery({
    queryKey: ['cert-history', activeOrgId],
    queryFn: () => certApi.history(activeOrgId!),
    enabled: !!activeOrgId,
  })

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl">
        <h1 className="text-3xl font-bold">Certificado de firma</h1>
        <p className="mt-2 text-sm text-fg-secondary">
          Por defecto, Taxor firma con su cert ECD bajo Contrato de Mandato. Puedes
          subir tu propio cert ECD (.p12) si tu compliance interno lo exige.
        </p>

        {(orgLoading || cert.isLoading) && (
          <p className="mt-6 inline-flex items-center gap-2 text-sm text-fg-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando estado del certificado…
          </p>
        )}
        {(orgError || cert.isError) && (
          <p className="mt-6 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100">
            No se pudo cargar el estado del certificado. Recarga la página o
            inténtalo más tarde.
          </p>
        )}

        {cert.data && !cert.data.has_byo_cert && (
          <div className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-8 w-8 text-green-600" />
              <div>
                <p className="font-semibold">Modo actual: Mandato Taxor</p>
                <p className="text-xs text-fg-secondary">
                  El cert ECD del PT firma tus documentos. Cero trámite ECD para ti.
                </p>
              </div>
            </div>
          </div>
        )}

        {cert.data?.has_byo_cert && cert.data.cert && (
          <ActiveCertCard cert={cert.data.cert} clientId={activeOrgId!} />
        )}

        {activeOrgId && <UploadCard clientId={activeOrgId} hasActive={!!cert.data?.has_byo_cert} />}

        {history.data && history.data.items.length > 0 && (
          <HistoryTable items={history.data.items} />
        )}
      </div>
    </AppShell>
  )
}

// ─── Cert activo ───────────────────────────────────────────────────

function ActiveCertCard({ cert, clientId }: { cert: CertInfo; clientId: string }) {
  const qc = useQueryClient()
  const [confirming, setConfirming] = useState(false)
  const [reason, setReason] = useState('')

  const archive = useMutation({
    mutationFn: () => certApi.archive(clientId, reason.trim() || 'archivado desde el portal de partners'),
    onSuccess: () => {
      toast.success('Certificado archivado — vuelves al mandato Taxor')
      setConfirming(false)
      setReason('')
      qc.invalidateQueries({ queryKey: ['cert', clientId] })
      qc.invalidateQueries({ queryKey: ['cert-history', clientId] })
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } } }
      toast.error(`No se pudo archivar: ${e?.response?.data?.message ?? 'error desconocido'}`)
    },
  })

  const days = cert.days_until_expiration
  const expiryTone =
    days <= 30
      ? 'text-red-700 dark:text-red-300'
      : days <= 90
        ? 'text-yellow-700 dark:text-yellow-300'
        : 'text-green-700 dark:text-green-300'

  return (
    <div className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-8 w-8 text-brand-600" />
          <div>
            <p className="font-semibold">Modo actual: certificado propio (BYO)</p>
            <p className="text-xs text-fg-secondary">Tus documentos se firman con este cert.</p>
          </div>
        </div>
        <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
          {cert.status}
        </span>
      </div>

      <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <CertField label="Titular (CN)" value={cert.subject_cn} />
        <CertField label="NIT del titular" value={cert.subject_nit ?? '—'} />
        <CertField label="Emisor (ECD)" value={cert.issuer_cn} />
        <CertField label="Algoritmo" value={cert.key_algorithm} />
        <CertField label="Válido desde" value={new Date(cert.valid_from).toLocaleDateString('es-CO')} />
        <CertField label="Válido hasta" value={new Date(cert.valid_to).toLocaleDateString('es-CO')} />
        <CertField label="Serial" value={cert.serial_hex} mono />
        <div>
          <dt className="text-xs font-medium text-fg-muted">Días para expirar</dt>
          <dd className={`font-semibold ${expiryTone}`}>{days}</dd>
        </div>
      </dl>

      {!confirming ? (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="mt-5 inline-flex items-center gap-1.5 rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950"
        >
          <Archive className="h-4 w-4" /> Archivar certificado
        </button>
      ) : (
        <div className="mt-5 rounded-md border border-red-300 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950">
          <p className="text-sm font-semibold text-red-900 dark:text-red-100">
            ¿Archivar el certificado activo?
          </p>
          <p className="mt-1 text-xs text-red-800 dark:text-red-200">
            Tus documentos volverán a firmarse con el cert del PT bajo el Contrato
            de Mandato. Puedes subir otro cert cuando quieras.
          </p>
          <input
            type="text"
            value={reason}
            maxLength={200}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Motivo (opcional)"
            className="mt-3 w-full rounded-md border border-border bg-bg-surface px-3 py-2 text-sm text-fg-primary placeholder:text-fg-muted focus:border-brand-500 focus:outline-none"
          />
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => archive.mutate()}
              disabled={archive.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {archive.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
              Confirmar archivo
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-fg-secondary hover:bg-bg-elevated"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function CertField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-medium text-fg-muted">{label}</dt>
      <dd className={`break-all text-fg-primary ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
    </div>
  )
}

// ─── Upload ────────────────────────────────────────────────────────

function UploadCard({ clientId, hasActive }: { clientId: string; hasActive: boolean }) {
  const qc = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [pwd, setPwd] = useState('')
  const [fieldError, setFieldError] = useState<string | null>(null)

  const upload = useMutation({
    mutationFn: () => certApi.upload(clientId, file!, pwd),
    onSuccess: () => {
      toast.success('Certificado subido y validado (issuer ECD + expiración OK)')
      setFile(null)
      setPwd('')
      setFieldError(null)
      qc.invalidateQueries({ queryKey: ['cert', clientId] })
      qc.invalidateQueries({ queryKey: ['cert-history', clientId] })
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string; error?: string } } }
      const msg = e?.response?.data?.message ?? e?.response?.data?.error ?? 'error desconocido'
      setFieldError(msg)
      toast.error(`No se pudo subir el certificado: ${msg}`)
    },
    onSettled: () => setPwd(''), // nunca dejar la password en el DOM
  })

  const handleUpload = () => {
    if (!file) {
      setFieldError('Selecciona el archivo .p12 de tu ECD.')
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      setFieldError('El .p12 no puede superar 2 MB.')
      return
    }
    setFieldError(null)
    upload.mutate()
  }

  return (
    <div className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
      <p className="text-sm font-semibold">
        {hasActive ? 'Reemplazar certificado' : '¿Quieres usar tu propio cert ECD?'}
      </p>
      <p className="mt-1 text-xs text-fg-secondary">
        Sube tu .p12 emitido por una ECD colombiana (Andes / Certicámara / GSE /
        Camerfirma / Thomas-Signe). Se valida issuer, vigencia y fortaleza de la llave
        (mínimo RSA-2048 o EC-P256).
        {hasActive && ' El cert actual se archiva automáticamente al subir uno nuevo.'}
      </p>

      <div className="mt-4 space-y-3">
        <div>
          <label className="block text-xs font-medium" htmlFor="p12-file">
            Archivo .p12
          </label>
          <input
            id="p12-file"
            type="file"
            accept=".p12,.pfx"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null)
              setFieldError(null)
            }}
            className="mt-1 block w-full text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium" htmlFor="p12-pwd">
            Contraseña del .p12
          </label>
          <input
            id="p12-pwd"
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            autoComplete="off"
            className="mt-1 w-full rounded-md border border-border bg-bg-surface px-3 py-2 text-sm text-fg-primary focus:border-brand-500 focus:outline-none"
          />
          <p className="mt-1 text-xs text-fg-muted">
            Puede estar vacía si tu .p12 no tiene contraseña. Solo se usa para
            validar y cifrar el cert — no se almacena en claro.
          </p>
        </div>

        {fieldError && (
          <p className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100">
            {fieldError}
          </p>
        )}

        <button
          type="button"
          onClick={handleUpload}
          disabled={upload.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {upload.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Subiendo…
            </>
          ) : (
            <>
              <Upload className="h-4 w-4" /> Subir y validar
            </>
          )}
        </button>
      </div>
    </div>
  )
}

// ─── Historial ─────────────────────────────────────────────────────

function HistoryTable({ items }: { items: CertInfo[] }) {
  return (
    <div className="mt-6">
      <h2 className="text-lg font-semibold">Historial de certificados</h2>
      <div className="mt-3 overflow-hidden rounded-xl border border-border bg-bg-surface">
        <table className="w-full text-sm">
          <thead className="bg-bg-elevated">
            <tr>
              <Th>Titular</Th>
              <Th>Emisor</Th>
              <Th>Vigencia</Th>
              <Th>Estado</Th>
              <Th>Subido</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-border">
                <td className="max-w-xs truncate px-4 py-3" title={c.subject_cn}>{c.subject_cn}</td>
                <td className="px-4 py-3 text-fg-secondary">{c.issuer_normalized}</td>
                <td className="px-4 py-3 text-xs text-fg-secondary">
                  {new Date(c.valid_from).toLocaleDateString('es-CO')} → {new Date(c.valid_to).toLocaleDateString('es-CO')}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      c.status === 'active'
                        ? 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300'
                        : 'bg-bg-elevated text-fg-muted'
                    }`}
                  >
                    {c.status === 'active' ? 'Activo' : 'Archivado'}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-fg-secondary">
                  {new Date(c.uploaded_at).toLocaleString('es-CO')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-4 py-3 text-left font-semibold text-fg-secondary">{children}</th>
}
